/**
 * InviteAcceptPage - Handle invite accept email links
 * 
 * Flow:
 * 1. Extract token from URL
 * 2. Validate token and show context
 * 3. Process acceptance
 * 4. Show result and remove token from URL
 */
import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { EmailActionResultPage } from "./EmailActionResultPage";
import api from "../lib/api";

export default function InviteAcceptPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading");
  const [title, setTitle] = useState(null);
  const [message, setMessage] = useState(null);
  const [contextInfo, setContextInfo] = useState(null);
  const [processed, setProcessed] = useState(false);

  // Remove token from URL after processing
  const removeTokenFromUrl = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Validate and process token
  useEffect(() => {
    if (!token) {
      setStatus("error");
      setTitle("Invalid link");
      setMessage("This invitation link is missing required information.");
      return;
    }

    if (processed) return;

    const processToken = async () => {
      try {
        // First validate the token
        const validateRes = await api.get(`/api/household/token/validate?token=${encodeURIComponent(token)}`);
        
        if (!validateRes.ok) {
          const errorData = await validateRes.json().catch(() => ({}));
          if (validateRes.status === 410) {
            setStatus("expired");
            setTitle("Invitation expired");
            setMessage("This invitation link is no longer valid. Please ask for a new invitation.");
          } else {
            setStatus("error");
            setMessage(errorData.detail || "This link is no longer valid.");
          }
          removeTokenFromUrl();
          return;
        }

        const validateData = await validateRes.json();

        if (!validateData.valid) {
          if (validateData.expired) {
            setStatus("expired");
            setTitle("Invitation expired");
            setMessage("This invitation link is no longer valid. Please ask for a new invitation.");
          } else {
            setStatus("error");
            setMessage("This link is no longer valid.");
          }
          removeTokenFromUrl();
          return;
        }

        // Set context info
        if (validateData.context) {
          setContextInfo(validateData.context);
          
          // Check if already handled
          if (validateData.context.status === "accepted") {
            setStatus("already_handled");
            setTitle("Already joined");
            setMessage("You have already joined this household.");
            removeTokenFromUrl();
            return;
          } else if (validateData.context.status !== "pending") {
            setStatus("already_handled");
            setTitle("Invitation unavailable");
            setMessage(`This invitation has been ${validateData.context.status}.`);
            removeTokenFromUrl();
            return;
          }
        }

        // Process the accept action
        const acceptRes = await api.post(`/api/household/token/invite/accept?token=${encodeURIComponent(token)}`);
        const acceptData = await acceptRes.json();

        if (acceptRes.ok) {
          if (acceptData.status === "needs_auth") {
            // Store token and redirect to login
            sessionStorage.setItem("pending_invite_token", token);
            setStatus("needs_auth");
            setTitle("Sign in to join");
            setMessage("Please sign in or create an account to accept this invitation.");
          } else if (acceptData.status === "already_accepted" || acceptData.status === "already_member") {
            setStatus("already_handled");
            setTitle("Already joined");
            setMessage(acceptData.message);
          } else {
            setStatus("success");
            setTitle("Welcome to the household");
            setMessage(acceptData.message || "You have successfully joined the household.");
          }
        } else if (acceptRes.status === 410) {
          setStatus("expired");
          setTitle("Invitation expired");
          setMessage(acceptData.detail || "This invitation is no longer valid.");
        } else {
          setStatus("error");
          setMessage(acceptData.detail || "Could not accept this invitation.");
        }

        removeTokenFromUrl();
        setProcessed(true);

      } catch (error) {
        console.error("Invite accept error:", error);
        setStatus("error");
        setMessage("Something went wrong. Please try again.");
        removeTokenFromUrl();
      }
    };

    processToken();
  }, [token, processed, removeTokenFromUrl]);

  // Determine buttons based on status
  let primaryButtonLabel = null;
  let primaryButtonAction = null;
  let secondaryButtonLabel = null;
  let secondaryButtonAction = null;

  if (status === "success") {
    primaryButtonLabel = "Continue setup";
    primaryButtonAction = "/partner-onboarding";
  } else if (status === "needs_auth") {
    primaryButtonLabel = "Sign In";
    primaryButtonAction = "/login";
    secondaryButtonLabel = "Create Account";
    secondaryButtonAction = "/register";
  } else if (status === "expired" || status === "error") {
    primaryButtonLabel = "Go to Dashboard";
    primaryButtonAction = "/";
  } else if (status === "already_handled") {
    primaryButtonLabel = "Go to Dashboard";
    primaryButtonAction = "/";
  }

  return (
    <EmailActionResultPage
      status={status}
      title={title}
      message={message}
      primaryButtonLabel={primaryButtonLabel}
      primaryButtonAction={primaryButtonAction}
      secondaryButtonLabel={secondaryButtonLabel}
      secondaryButtonAction={secondaryButtonAction}
      contextInfo={contextInfo}
      showHomeButton={false}
    />
  );
}
